import { z } from 'zod'

/** Robinhood Chain (Arbitrum Orbit L2), the production chain. */
export const CHAIN_ID = 4663 as const
/** Local anvil chain (contracts/scripts/anvil-local.mjs). */
export const ANVIL_CHAIN_ID = 31337 as const
/**
 * Chain id on the wire: any positive integer, so development against anvil
 * (31337) validates. Production is always `CHAIN_ID`.
 */
export const ChainId = z.number().int().positive()

/** Native ETH is represented by the zero address everywhere. */
export const NATIVE_ETH = '0x0000000000000000000000000000000000000000' as const
/** WETH on Robinhood Chain. */
export const WETH = '0x0bd7d308f8e1639fab988df18a8011f41eacad73' as const

const HEX40 = /^0x[0-9a-fA-F]{40}$/
const HEX64 = /^0x[0-9a-fA-F]{64}$/

/** EVM address. Accepts any case on input, always lower-case on output. */
export const Address = z
  .string()
  .regex(HEX40, 'expected 0x-prefixed 20-byte hex address')
  .transform((s) => s.toLowerCase() as `0x${string}`)
export type Address = z.output<typeof Address>

/** 32-byte hash (tx hash, block hash), lower-case on output. */
export const Hash32 = z
  .string()
  .regex(HEX64, 'expected 0x-prefixed 32-byte hex')
  .transform((s) => s.toLowerCase() as `0x${string}`)
export type Hash32 = z.output<typeof Hash32>

/** Arbitrary 0x-prefixed hex data (calldata). */
export const HexData = z
  .string()
  .regex(/^0x([0-9a-fA-F]{2})*$/, 'expected 0x-prefixed even-length hex')
  .transform((s) => s.toLowerCase() as `0x${string}`)
export type HexData = z.output<typeof HexData>

/** Non-negative integer as a decimal string (wei-scale amounts, token units). */
export const WeiString = z.string().regex(/^(0|[1-9][0-9]*)$/, 'expected a non-negative decimal integer string')
export type WeiString = z.output<typeof WeiString>

/** Block number as a decimal string (bigint in code). */
export const BlockNumber = WeiString
export type BlockNumber = WeiString

/** Micro-credit amount (1 credit = USD 0.01 = 1 000 000 micro) as a decimal integer string. */
export const MicroString = z.string().regex(/^-?(0|[1-9][0-9]*)$/, 'expected a decimal integer string')
export type MicroString = z.output<typeof MicroString>

/** USD amount: decimal string with at most 6 fraction digits. May be negative (pnl). */
export const UsdString = z
  .string()
  .regex(/^-?(0|[1-9][0-9]*)(\.[0-9]{1,6})?$/, 'expected a decimal string with at most 6 fraction digits')
export type UsdString = z.output<typeof UsdString>

/** Ratio or percentage as a decimal string (win rate, drawdown pct). */
export const DecimalString = z.string().regex(/^-?(0|[1-9][0-9]*)(\.[0-9]+)?$/, 'expected a decimal string')
export type DecimalString = z.output<typeof DecimalString>

/** ISO-8601 UTC timestamp (`Z` or offset accepted), e.g. 2026-09-24T12:00:00.000Z. */
export const IsoTimestamp = z.iso.datetime({ offset: true })
export type IsoTimestamp = z.output<typeof IsoTimestamp>

export const Uuid = z.uuid()
export type Uuid = z.output<typeof Uuid>

/** Handle for users and agents: 3-24 chars of [a-z0-9_]. */
export const Handle = z.string().regex(/^[a-z0-9_]{3,24}$/, 'handle must be 3-24 chars of a-z, 0-9, _')
export type Handle = z.output<typeof Handle>

/** Opaque pagination cursor (base64url). */
export const Cursor = z.string().min(1).max(512).regex(/^[A-Za-z0-9_-]+={0,2}$/, 'invalid cursor')
export type Cursor = z.output<typeof Cursor>

/** Evidence object id: lower-case hex sha256 (no 0x). */
export const EvidenceId = z.string().regex(/^[0-9a-f]{64}$/, 'expected lower-case hex sha256')
export type EvidenceId = z.output<typeof EvidenceId>

/** Basis points 0..10000. */
export const Bps = z.number().int().min(0).max(10_000)

/** Idempotency key supplied by clients or agents. */
export const IdempotencyKey = z.string().min(1).max(200)

export const PageLimit = z.coerce.number().int().min(1).max(200).default(50)

/** Paginated list `{ items, nextCursor }`. */
export function Page<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), nextCursor: Cursor.nullable() })
}
export type Page<T> = { items: T[]; nextCursor: Cursor | null }

function toBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text)
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
function fromBase64Url(b64: string): string {
  const std = b64.replace(/-/g, '+').replace(/_/g, '/')
  const bin = atob(std + '='.repeat((4 - (std.length % 4)) % 4))
  return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)))
}

/** Encode a keyset position (e.g. `[createdAt, id]`) as an opaque cursor. Works in Node and browsers. */
export function encodeCursor(parts: readonly (string | number)[]): string {
  return toBase64Url(JSON.stringify(parts))
}
/** Decode a cursor made by `encodeCursor`; returns null when malformed. */
export function decodeCursor(cursor: string): (string | number)[] | null {
  try {
    const v: unknown = JSON.parse(fromBase64Url(cursor))
    return Array.isArray(v) && v.every((x) => typeof x === 'string' || typeof x === 'number') ? v : null
  } catch {
    return null
  }
}
